/**
 * État du hors-ligne partagé par toute l'application : connexion au serveur, date des données affichées
 * quand elles viennent de l'appareil, actions en attente d'envoi, mode hors ligne choisi par l'utilisateur.
 */
import { useSyncExternalStore } from "react";

export interface OfflineStatus {
  /** Faux dès qu'une requête n'a pas pu joindre le serveur ; vrai à la première réponse. */
  online: boolean;
  /** Date (ms) des plus anciennes données servies depuis l'appareil depuis la coupure ; null si aucune. */
  cachedAt: number | null;
  /** Actions en attente d'envoi. */
  pending: number;
  /** Actions refusées ou à vérifier (doublon possible). */
  review: number;
  /** Envoi en cours. */
  syncing: boolean;
  /** Mode hors ligne choisi (« Passer en mode hors ligne ») : plus aucun appel au serveur jusqu'au retour. */
  forced: boolean;
  /** Préparation du mode hors ligne en cours : lectures faites sur le total. */
  preparing: { done: number; total: number; label?: string } | null;
}

/** Choix gardé d'une ouverture à l'autre (préférence de l'appareil). */
const FORCED_KEY = "waitechedu.offline-mode";

function readForced(): boolean {
  try {
    return localStorage.getItem(FORCED_KEY) === "1";
  } catch {
    return false;
  }
}

const forcedAtStart = readForced();
let status: OfflineStatus = {
  online: !forcedAtStart,
  cachedAt: null,
  pending: 0,
  review: 0,
  syncing: false,
  forced: forcedAtStart,
  preparing: null,
};
const listeners = new Set<() => void>();

export function setOfflineStatus(patch: Partial<OfflineStatus>): void {
  const next = { ...status, ...patch };
  if (Object.keys(next).every((k) => next[k as keyof OfflineStatus] === status[k as keyof OfflineStatus])) return;
  status = next;
  listeners.forEach((listener) => listener());
}

export function getOfflineStatus(): OfflineStatus {
  return status;
}

/** Mode hors ligne choisi par l'utilisateur (aucun appel au serveur). */
export function isForcedOffline(): boolean {
  return status.forced;
}

/** Active ou quitte le mode hors ligne choisi. */
export function setForcedOffline(on: boolean): void {
  try {
    if (on) localStorage.setItem(FORCED_KEY, "1");
    else localStorage.removeItem(FORCED_KEY);
  } catch {
    /* stockage indisponible : le choix vaut pour cette ouverture seulement */
  }
  setOfflineStatus(on ? { forced: true, online: false } : { forced: false });
}

/** Réponse reçue du serveur : on est en ligne, les données affichées sont à jour. */
export function markOnline(): void {
  if (!status.online && !status.forced) setOfflineStatus({ online: true, cachedAt: null });
}

/** Serveur injoignable ; `cachedAt` : date des données de l'appareil utilisées à la place. */
export function markOffline(cachedAt?: number): void {
  const oldest = cachedAt === undefined ? status.cachedAt : Math.min(cachedAt, status.cachedAt ?? cachedAt);
  setOfflineStatus({ online: false, cachedAt: oldest });
}

export function subscribeOfflineStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useOfflineStatus(): OfflineStatus {
  return useSyncExternalStore(subscribeOfflineStatus, getOfflineStatus, getOfflineStatus);
}
